/**
 * Close a details / edit surface when the unit GET fails, with a catalogued toast.
 */
import * as React from 'react';
import { toast } from '@/components/ui/toast';
import type { CatalogErrorCode } from '@/constants/errors-catalog';
import { getErrorMessage } from '@/utils/errors-utils';

/** Inputs for {@link useUnitGetNotFound}. */
export interface UnitGetNotFoundParams {
  isError: boolean;
  error: unknown;
  catalogKey: CatalogErrorCode;
  onClose?: () => void;
  entityId?: string | null;
}

/** True when the error is an HTTP 404. */
export function isHttpNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { response?: { status?: number } }).response?.status === 404;
}

/** React Query retry: stop immediately on 404, else up to 3 failures. */
export function shouldRetryUnitGet(failureCount: number, error: Error): boolean {
  if (isHttpNotFound(error)) return false;
  return failureCount < 3;
}

/** Toast the not-found catalog key on 404, else the mapped error, then close. */
export function useUnitGetNotFound(params: UnitGetNotFoundParams): void {
  const { isError, error, catalogKey, onClose, entityId } = params;
  const handledKey = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isError) {
      handledKey.current = null;
      return;
    }
    const onceKey = entityId ?? catalogKey;
    if (handledKey.current === onceKey) return;
    handledKey.current = onceKey;
    toast.error(getErrorMessage(isHttpNotFound(error) ? catalogKey : error));
    onClose?.();
  }, [isError, error, catalogKey, onClose, entityId]);
}
